import BusinessApi from '@/api/Business'
import type { BusinessFullData } from '@/api/Business/types.ts'
import type { FetchParams, ParamType } from '@/api/types.ts'
import {
  queryOptions,
  useQueries,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useCallback } from 'react'

export function useFetchBusiness(params: FetchParams, enabled = true) {
  return useQuery({
    queryKey: ['fetch-business', params],
    queryFn: () => BusinessApi.fetch([], params),
    enabled,
  })
}

export function getBusinessQueryOptions(id: ParamType) {
  return queryOptions({
    queryKey: ['get-business', id],
    queryFn: () => BusinessApi.get(id),
  })
}

export function useGetBusiness(id: ParamType) {
  return useQuery(getBusinessQueryOptions(id))
}

const UNAVAILABLE_BUSINESS_STATUSES = new Set([403, 404])

function isUnavailableBusinessError(error: Error | null) {
  return (
    isAxiosError(error) &&
    UNAVAILABLE_BUSINESS_STATUSES.has(error.response?.status ?? 0)
  )
}

function collectBusinessesAvailability(
  ids: number[],
  results: UseQueryResult<BusinessFullData>[]
) {
  return {
    businesses: results.flatMap(({ data }) => (data ? [data] : [])),
    unavailableIds: ids.filter((_, index) =>
      isUnavailableBusinessError(results[index]?.error ?? null)
    ),
  }
}

export function useGetBusinesses(ids: number[]) {
  const combine = useCallback(
    (results: UseQueryResult<BusinessFullData>[]) =>
      collectBusinessesAvailability(ids, results),
    [ids]
  )
  return useQueries({
    queries: ids.map((id) => getBusinessQueryOptions(id)),
    combine,
  })
}
